import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const THEME_KEY = 'plum-theme-preference';

/**
 * Returns the resolved theme ('light' or 'dark') based on preference.
 * 'system' resolves to the OS preference.
 */
function getSystemTheme() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'dark';
  }
}

function resolveTheme(preference) {
  if (preference === 'system') {
    return getSystemTheme();
  }
  return preference;
}

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) || 'system';
    } catch {
      return 'system';
    }
  });

  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(preference));

  // Apply theme to document
  const applyTheme = useCallback((theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-bs-theme', theme);
    setResolvedTheme(theme);
  }, []);

  // Watch for system preference changes when mode is 'system'
  useEffect(() => {
    applyTheme(resolveTheme(preference));

    if (preference === 'system') {
      try {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => {
          applyTheme(e.matches ? 'dark' : 'light');
        };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
      } catch {
        // matchMedia not available (e.g., test environment)
      }
    }
  }, [preference, applyTheme]);

  const setThemePreference = useCallback((newPref) => {
    setPreference(newPref);
    try {
      localStorage.setItem(THEME_KEY, newPref);
    } catch {
      // localStorage unavailable
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setThemePreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
